import { moment } from 'config/moment.config'
import { times, divide } from 'number-precision'

export const DEFAULT_PRECISION_MONEY = 2

export const formatDate = (originalDateString) => {
  const date = new Date(originalDateString)

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ]
  const day = date.getDate()
  const monthIndex = date.getMonth()
  const year = date.getFullYear()
  const formattedDate = `${day} ${months[monthIndex]} ${year}`
  return formattedDate
}

export const formatTime = (originalDateString) => {
  const date = new Date(originalDateString)

  const hours = date.getHours()
  const minutes = date.getMinutes()

  const ampm = hours >= 12 ? 'PM' : 'AM'
  const formattedHours = hours % 12 || 12 // Convert 0 to 12 for 12 AM

  const formattedTime = `${formattedHours}:${minutes < 10 ? '0' : ''}${minutes} ${ampm}`

  return formattedTime
}

/**
 * Parses payload object from jwt
 * @param {string} token
 * @returns {Object}
 */
export const getPayloadFromToken = (token) => {
  if (token) {
    return JSON.parse(atob(token.split('.')[1]))
  }
}

/**
 * Generate a random card number between [1,52]
 * @returns {number}
 */
export const randomCardGenerate = () => {
  return Math.floor(Math.random() * 52) + 1
}

/**
 *
 * @param {Number} n
 * @returns {Number} factorial of n
 */
export const factorial = n => {
  let fact = 1
  for (let i = 2; i <= n; i++) {
    fact *= i
  }
  return fact
}

/**
 *
 * @param {Number} n
 * @param {Number} r
 * @returns {Number} combination of n over r
 */
export const combination = (n, r) => {
  if (r > n) {
    return 0
  }
  return factorial(n) / (factorial(r) * factorial(n - r))
}

/**
 *
 * @param {Number} numberOfCoins
 * @param {Number} minimumChosenOutcome
 * @returns {Number} probability of minimum X number of favorable outcomes
 */
export const getCoinOutcomeProbability = (numberOfCoins, minimumChosenOutcome) => {
  const n = numberOfCoins - minimumChosenOutcome
  let sum = 0
  for (let i = 0; i <= n; i++) {
    sum += combination(numberOfCoins, i)
  }
  return sum / 2 ** numberOfCoins
}

/**
 * To calculate flip coin potential win
 * @param {number} betAmount
 * @param {number} numberOfCoins
 * @param {number} minimumChosenOutcome
 * @param {number} houseEdge
 * @returns {number}
 */
export const getFlipCoinPotentialWin = (betAmount, numberOfCoins, minimumChosenOutcome, houseEdge) => {
  const probability = getCoinOutcomeProbability(numberOfCoins, minimumChosenOutcome)
  const odds = (1 / probability) * (1 - houseEdge / 100)
  return +Number((betAmount * odds)).toFixed(2)
}

/**
 *
 * @param {string} s
 * @returns {Number} count of one
 */
export const countOnes = (s) => {
  let count = 0
  for (const i of s) {
    if (i === '1') {
      count++
    }
  }
  return count
}

/**
 *
 * @param  {...Array} a Specify arrays to do cartesian product
 * @returns {Array.<Array.<number[]>>} Returns the cartesian product of given arrays
 */
export const cartesianProductOfArrays = (...a) => a.reduce((a, b) => a.flatMap(d => b.map(e => [d, e].flat())))

export const generateClientSeed = () => {
  const availableChars = '0123456789abcdef'
  let seed = ''
  for (let i = 0; i < 32; i++) {
    seed += availableChars[Math.floor(Math.random() * availableChars.length)]
  }
  return seed
}

/**
 *
 * @param {number} value
 * @param {number} precision
 * @returns {number | string} Returns precision value for specified decimal places
 */
export const getPrecision = (value, precision = DEFAULT_PRECISION_MONEY) => {
  let strValue = String(value)
  if (strValue.includes('e') || strValue.includes('E')) {
    if (value >= 1e21) return value
    strValue = value.toFixed(precision + 20) // expand safely
  }
  const [int, dec = ""] = strValue.split(".")
  return Number(
    int + "." + dec.slice(0, precision).padEnd(precision, "0")
  )
}

/**
 * To convert a numeric value to standard precision
 * @param {number} amount amount to be converted to precision
 * @param {number} precision digits to be considered for precision
 * @returns {number}
 */
export const getPrecisionNumber = (amount, precision = DEFAULT_PRECISION_MONEY) => {
  const precisionDivide = Math.pow(10, precision)
  const result = divide(Math.trunc(times(amount, precisionDivide)), precisionDivide)
  return result || 0
}

/**
 *
 * @param {number} x
 * @returns {string} valid million based comma value in string
 */
export const numberWithCommas = (x) => {
  if (x === null || x === undefined || Number(x) === Number.isNaN) {
    return ''
  }
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * Get Random Integer from min to max (both inclusive)
 * @param {*} max
 * @returns
 */
export function getRandomInt (min, max) {
  min = Math.ceil(min)
  max = Math.floor(max + 1)
  return Math.floor(Math.random() * (max - min) + min)
}
/**
 * Durstenfeld array shuffling algorithm
 * @param {number} array
 */
export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

export const copyToClipboard = (text) => {
  if (window.clipboardData && window.clipboardData.setData) {
    // Internet Explorer-specific code path to prevent textarea being shown while dialog is visible.
    return window.clipboardData.setData('Text', text)
  }
  if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
    const textarea = document.createElement('textarea')
    textarea.textContent = text
    textarea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textarea)
    textarea.select()
    try {
      return document.execCommand('copy') // Security exception may be thrown by some browsers.
    } catch (ex) {
      console.warn('Copy to clipboard failed.', ex)
      return false
    } finally {
      document.body.removeChild(textarea)
    }
  }
}

const invalidCharacters = ['e', 'E', '+', '-', '(', ')', '%', '^', '&', '*', '+', '-', '=', '[', ']', '{', '}', '|', '\\', ':', ';', '"', "'", '<', '>', '?', '/', '`', '~', '@', ',', '!', '₹']

export const blockInvalidChar = e => {
  // Allow numbers (0-9) and only one decimal point
  if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && e.target.value.includes('.'))) {
    if (e.key !== 'Backspace') {
      e.preventDefault()
    }
  }

  invalidCharacters.includes(e.key) && e.preventDefault()
}

export const blockInvalidCharForBetCount = e => {
  const invalidChars = ['.', ...invalidCharacters]
  if (invalidChars.includes(e.key)) {
    e.preventDefault()
  }

  if (e.shiftKey === true) {
    if (e.which === 9) {
      return true
    }
    return false
  }
  if (e.which > 57) {
    return false
  }
  if (e.which === 32) {
    return false
  }
  return true
}

export const blockInvalidCharNoDecimal = (e) => {
  // Allowed keys: numbers (0-9), Backspace, Arrow keys, Delete, Tab, Home, End
  if (!/^[0-9]$/.test(e.key) && !['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End'].includes(e.key)) {
    e.preventDefault()
  }
}

export const blockSpaceChar = e => e.key === ' ' && e.preventDefault()

export const formatDateTime = (dateString) => {
  // Create a new Date object from the given date string
  const date = new Date(dateString)

  // Extract hours, minutes, and determine AM/PM
  let hours = date.getUTCHours()
  const minutes = date.getUTCMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'

  // Convert 24-hour format to 12-hour format
  hours = hours % 12
  hours = hours || 12 // If hours equals 0, change it to 12

  // Format minutes to be 2 digits
  const minutesFormatted = minutes < 10 ? '0' + minutes : minutes

  // Extract month, day, and year
  const month = date.getUTCMonth() + 1 // Months are zero-indexed
  const day = date.getUTCDate()
  const year = date.getUTCFullYear()

  // Return formatted string
  return `${hours}:${minutesFormatted} ${ampm}  ${month}/${day}/${year}`
}

export const restrictDecimalPlaces = (enteredValue) => {
  const value = String(enteredValue)
  const decimalIndex = value.indexOf('.')

  if (decimalIndex !== -1) {
    // Split into two parts: before the decimal and after the decimal
    const beforeDecimal = value.slice(0, decimalIndex) // Before decimal part
    let afterDecimal = value.slice(decimalIndex + 1) // After decimal part

    // If there are more than two digits after the decimal, replace the second digit
    if (afterDecimal.length > 2) {
      afterDecimal = afterDecimal.slice(0, 1) + afterDecimal.slice(2, 3) // Keep first and third digits after decimal
    }

    // Rebuild the value with the updated decimal part
    const finalValue = beforeDecimal + '.' + afterDecimal
    return +finalValue
  }

  return +value // If there's no decimal point, return the value as is
}

export const cryptoIcon = (id) => {
  switch (id) {
    case '1':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/1.png'
    case '2':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/1027.png'
    case '3':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/5994.png'
    case '4':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/1839.png'
    case '5':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/52.png'
    case '6':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/74.png'
    case '7':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/16116.png'
    case '8':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/1831.png'
    case '9':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/5805.png'
    case '10':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/2010.png'
    case '11':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/6636.png'
    case '12':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/6636.png'
    case '13':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/5994.png'
    case '14':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/3890.png'
    case '15':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/18876.png'
    case '16':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/21794.png'
    case '17':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/3513.png'
    case '18':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/1437.png'
    case '19':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/11841.png'
    case '20':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/24478.png'
    case '21':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/1831.png'
    case '22':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/13502.png'
    case '23':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/5805.png'
    case '24':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/28301.png'
    case '25':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/7226.png'
    case '26':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/20396.png'
    case '27':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/23121.png'
    case '28':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/28177.png'
    case '29':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/23095.png'
    case '30':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/23149.png'
    case '31':
      return 'https://s2.coinmarketcap.com/static/img/coins/64x64/22861.png'
    default:
      return ''
  }
}

export const startTime = (time) => {
  return moment().startOf(time).format()
}

export const endTime = (time) => {
  return moment().endOf(time).format()
}

export const formatNumber = (value, smallPrecision = 5, normalPrecision = 2) => {
  const num = +value
  const precision = num < 0.1 ? smallPrecision : normalPrecision
  return num.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  })
}

export async function createBurstAndEntryPriceLine (burstPrice = null, entryPrice = null) {
  if (await window?.entryPriceShape) {
    window?.tvWidget?.activeChart().removeEntity(window.entryPriceShape)
  }
  if (await window?.burstPriceShape) window?.tvWidget?.activeChart().removeEntity(window.burstPriceShape)

  const finalBurstPrice = burstPrice !== null ? +burstPrice : +window?.burstPrice
  const finalEntryPrice = entryPrice !== null ? +entryPrice : +window?.entryPrice

  if (!finalEntryPrice && !finalBurstPrice) return
  window.entryPriceShape = await window?.tvWidget?.activeChart().createShape(
    {
      price: finalEntryPrice, // window.entryPrice,
      time: Math.floor(Date.now() / 1000)
    },
    {
      shape: 'horizontal_line',
      text: 'Entry Price',
      overrides: {
        linecolor: '#879096',
        showLabel: true,
        axisLabelVisible: true,
        fontsize: 15,
        style: 2,
        textcolor: '#879096'

      },
      lock: true,
      disableSelection: true,
      disableContextMenu: true,
      disableDrag: true
    }
  )
  // BUST PRICE LINE
  window.burstPriceShape = await window?.tvWidget?.activeChart().createShape(
    { price: finalBurstPrice },
    {
      shape: 'horizontal_line',
      overrides: {
        linecolor: '#FF0000',
        showLabel: true,
        axisLabelVisible: true,
        axisLabelText: 'Burst Price',
        fontsize: 15,
        style: 2,
        textcolor: '#FF0000'
      },
      text: 'Bust Price',
      lock: true,
      disableSelection: true,
      disableContextMenu: true,
      disableDrag: true
    }
  )
}

// This function called when cashout or Burst happens
export async function removeBurstAndEntryPriceLine (entryPrice = null, bustPrice = null) {
  try {
  // TO remove burst and entry price
    if (window?.entryPrice) delete window.entryPrice
    if (window?.burstPrice) delete window.burstPrice

    // TO remove candlestic line
    const chart = window?.tvWidget?.activeChart?.()
    if (chart?.removeEntity) {
      if (window?.entryPriceShape) chart.removeEntity(window.entryPriceShape)
      if (window?.burstPriceShape) chart.removeEntity(window.burstPriceShape)
      if (entryPrice) chart.removeEntity(entryPrice)
      if (bustPrice) chart.removeEntity(bustPrice)
      delete window?.entryPriceShape
      delete window?.burstPriceShape
    }

    // TO remove Tick line
    const series = window?.lineSeriesRef
    if (series?.removePriceLine) {
      if (window?.entryLineTicker) series.removePriceLine(window.entryLineTicker)
      if (window?.burstLineTicker) series.removePriceLine(window.burstLineTicker)
      if (entryPrice) series.removePriceLine(entryPrice)
      if (bustPrice) series.removePriceLine(bustPrice)
      delete window?.entryLineTicker
      delete window?.burstLineTicker
    }
  } catch (error) {
    console.error('Error removing burst and entry price lines:')
  }
}

export const burstAndEntryLineInTicker = (burstPrice = null, entryPrice = null) => {
  if (window?.burstLineTicker) {
    window.lineSeriesRef.removePriceLine(window.burstLineTicker)
    delete window.burstLineTicker
  }

  if (window?.entryLineTicker) {
    window.lineSeriesRef.removePriceLine(window.entryLineTicker)
    delete window.entryLineTicker
  }

  const finalBurstPrice = burstPrice !== null ? +burstPrice : +window.burstPrice
  const finalEntryPrice = entryPrice !== null ? +entryPrice : +window.entryPrice

  if (finalBurstPrice && finalEntryPrice) {
    window.burstLineTicker = window.lineSeriesRef.createPriceLine({
      price: finalBurstPrice,
      color: '#FF0000',
      lineWidth: 2,
      lineStyle: 0,
      title: 'Bust Price'
    })

    window.entryLineTicker = window.lineSeriesRef.createPriceLine({
      price: finalEntryPrice,
      color: '#879096',
      lineWidth: 2,
      lineStyle: 0,
      axisLabelVisible: true,
      title: 'Entry Price'
    })
  }
}
